import { Button, Grid, TableRow, makeStyles } from '@material-ui/core';
import { ArrowDropDown, EditLocation } from '@material-ui/icons';
import { Fragment, memo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import useRole from '../hooks/useRole';
import { SourceRegisterPotentialDTO, ValuationCountryCode } from '../modules/generated/api';
import { MAX_NUMBER_CHECKABLE } from '../modules/global-vars';
import { USER_EXTRA_COUNTRIES } from '../modules/valuation-country-helpers';
import UserRole from '../types/UserRoles';

import CarDetailTableHeadCell from './CarDetailTableHeadCell';
import CarDetailTableUnitValueCell from './CarDetailTableUnitValueCell';
import UnitValue from './UnitValue';
import ValuationCountryIcon from './ValuationCountryIcon';
import ValuationCountrySwitcher from './ValuationCountrySwitcher';

const useStyles = makeStyles((theme) => ({
  tableCellEnd: {
    justifyContent: 'flex-end',
  },
  highlightGross: {
    backgroundColor: '#F0F0F0',
    '& span': {
      fontWeight: 'bold',
    },
  },
  marginRight: {
    marginRight: theme.spacing(1),
  },
  checkBoxRootBtn: {
    fontSize: '0.75rem',
    paddingRight: 0,
  },
  valuationCountryWrapper: {
    float: 'right',
    display: 'flex',
    alignItems: 'center',
  },
}));

type PackageDetailPotentialsProps = {
  comparisonCountries: Set<ValuationCountryCode>;
  setCheckedCountries: (countries: Set<ValuationCountryCode>) => void;
  countryPotentials: { [key: string]: SourceRegisterPotentialDTO };
};

const PackageDetailPotentials = ({
  comparisonCountries,
  setCheckedCountries,
  countryPotentials,
}: PackageDetailPotentialsProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { hasRole, objectToken } = useRole();
  const isAdmin = hasRole(UserRole.ADMIN);
  const userId = objectToken?.user_id;
  const isWhitelisted = userId != null && userId in USER_EXTRA_COUNTRIES;
  const countriesArray = Array.from(comparisonCountries);

  return (
    <>
      <TableRow>
        <CarDetailTableHeadCell>
          {isAdmin || isWhitelisted ? (
            <ValuationCountrySwitcher
              rootButton={
                <Button size="small" variant="outlined" className={classes.checkBoxRootBtn}>
                  <EditLocation fontSize="small" className={classes.marginRight} />
                  {t('navigation.changeValuationCountry_plural')} ({comparisonCountries.size}/{MAX_NUMBER_CHECKABLE})
                  <ArrowDropDown fontSize="small" />
                </Button>
              }
              disableValuationTypeSubmenu
              checkboxMode
              checkedCountries={comparisonCountries}
              setCheckedCountries={setCheckedCountries}
              maxNumberCheckable={MAX_NUMBER_CHECKABLE}
            />
          ) : (
            <Trans t={t} i18nKey="navigation.changeValuationCountry" />
          )}
        </CarDetailTableHeadCell>
        <CarDetailTableUnitValueCell>
          <Grid container className={classes.tableCellEnd} spacing={1}>
            {countriesArray.map((country) => (
              <Grid item xs={4} key={`flag-${country}`}>
                <div className={classes.valuationCountryWrapper}>
                  <ValuationCountryIcon valuationCountry={country} disableLocationIcon />
                </div>
              </Grid>
            ))}
          </Grid>
        </CarDetailTableUnitValueCell>
      </TableRow>

      <TableRow>
        <CarDetailTableHeadCell>{t('car.prices.potentialPriceMin')}</CarDetailTableHeadCell>
        <CarDetailTableUnitValueCell>
          <Grid container className={classes.tableCellEnd} spacing={1}>
            {countriesArray.map((country) => {
              const potentials = countryPotentials[country];
              return (
                <Fragment key={`potentialMin-${country}`}>
                  <Grid item xs={4}>
                    <UnitValue
                      value={potentials?.potentialMinAbsolute?.amount}
                      unit={potentials?.potentialMinAbsolute?.currency}
                    />
                    {potentials?.potentialMinRelative !== undefined && (
                      <>
                        {' '}
                        (<UnitValue value={potentials.potentialMinRelative} unit="%" />)
                      </>
                    )}
                  </Grid>
                </Fragment>
              );
            })}
          </Grid>
        </CarDetailTableUnitValueCell>
      </TableRow>

      <TableRow>
        <CarDetailTableHeadCell>{t('car.prices.potentialPriceMinAverage')}</CarDetailTableHeadCell>
        <CarDetailTableUnitValueCell>
          <Grid container className={classes.tableCellEnd} spacing={1}>
            {countriesArray.map((country) => {
              const potentials = countryPotentials[country];
              return (
                <Fragment key={`potentialAvg-${country}`}>
                  <Grid item xs={4}>
                    <UnitValue
                      value={potentials?.potentialMinMeanAbsolute?.amount}
                      unit={potentials?.potentialMinMeanAbsolute?.currency}
                    />
                    {potentials?.potentialMinMeanRelative !== undefined && (
                      <>
                        {' '}
                        (<UnitValue value={potentials.potentialMinMeanRelative} unit="%" />)
                      </>
                    )}
                  </Grid>
                </Fragment>
              );
            })}
          </Grid>
        </CarDetailTableUnitValueCell>
      </TableRow>
    </>
  );
};

export default memo(PackageDetailPotentials);
